import type { GetServerSideProps, InferGetServerSidePropsType } from "next";
import { ContentPageBlocks } from "@/components/content-page-blocks";
import { PageSeoHead } from "@/components/page-seo-head";
import { getFooter, getHomePage, getMenuPage, getNavigation } from "@/lib/api";
import {
  buildStorefrontStructuredData,
  buildWebSiteSchema,
} from "@/lib/seo-organization";
import { toAbsoluteMediaUrl } from "@/lib/seo-url";
import type { StorefrontShellData } from "@/lib/storefront-shell-types";
import {
  getStorefrontCanonicalOrigin,
  getStorefrontSiteConfig,
  resolveStorefrontSiteCode,
  type StorefrontSiteCode,
} from "@/lib/storefront-site";
import type {
  ContentBlock,
  HeroSliderBlock,
  NavigationItem,
  SeoData,
} from "@/lib/storefront-types";

type HomePageProps = {
  navigationItems: NavigationItem[];
  shellData: StorefrontShellData;
  layoutVariant: "home";
  siteCode: StorefrontSiteCode;
  blocks: ContentBlock[];
  seo: SeoData | null;
};

export const getServerSideProps: GetServerSideProps<HomePageProps> = async (
  context,
) => {
  const siteCode = resolveStorefrontSiteCode({
    configuredSiteCode: process.env.STOREFRONT_SITE,
    hostname: context.req.headers.host,
  });
  const [navigation, menuPage, footer, homePage] = await Promise.all([
    getNavigation(siteCode),
    getMenuPage(siteCode),
    getFooter(siteCode),
    getHomePage(siteCode),
  ]);

  return {
    props: {
      navigationItems: navigation.items,
      shellData: {
        footer,
        menuCategories: menuPage.categories,
        loginRegisterInsertAfter: navigation.loginRegisterInsertAfter,
      },
      layoutVariant: "home",
      siteCode,
      blocks: homePage?.blocks ?? [],
      seo: homePage?.seo ?? null,
    },
  };
};

export default function HomePage({
  blocks,
  seo,
  shellData,
  siteCode,
}: InferGetServerSidePropsType<typeof getServerSideProps>) {
  const origin = getStorefrontCanonicalOrigin(siteCode);
  // The home page carries the site-wide graph roots for both brands: the org
  // node (#organization) plus the WebSite node whose publisher references it.
  // The home page's CMS SEO image doubles as the Restaurant photo (`image`) —
  // a real photo, not the SVG logo, per Google's LocalBusiness guidance.
  const structuredData = [
    buildStorefrontStructuredData(
      siteCode,
      shellData.footer ?? null,
      toAbsoluteMediaUrl(seo?.seoImage?.url, origin) ?? null,
    ),
    buildWebSiteSchema(origin, getStorefrontSiteConfig(siteCode).brandName),
  ];
  // The page H1 normally comes from the first hero slider's headline. When the
  // CMS homepage carries no hero block — or the first slider's slide 0 has no
  // non-empty headline, in which case the slider renders no H1 (it demotes an
  // empty slide-0 heading to an h2) — fall back to a visually-hidden H1 so the
  // page never ships without one. The trim() mirror of the slider's own guard
  // keeps this from ever producing a second H1.
  const firstHeroSlider = blocks.find(
    (block): block is HeroSliderBlock => block.kind === "heroSlider",
  );
  const heroRendersH1 = Boolean(firstHeroSlider?.slides[0]?.headline?.trim());
  const fallbackHeadline =
    seo?.pageTitle?.trim() ||
    seo?.seoTitle?.trim() ||
    getStorefrontSiteConfig(siteCode).brandName;

  return (
    <>
      <PageSeoHead
        siteCode={siteCode}
        seo={seo}
        structuredData={structuredData}
      />
      {!heroRendersH1 ? <h1 className="sr-only">{fallbackHeadline}</h1> : null}
      <ContentPageBlocks blocks={blocks} heroHeadingLevel={1} />
    </>
  );
}
